import { ThingType } from '@eagle/core-data-types';
import { Stack, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@ethings/ui-toolkit';
import { Paper } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FetchOneOfAll } from '../../components';
import { T_ONE } from '../../constants';
import { useSmallScreen } from '../../hooks';
import { CacheDataTypes } from '../../types';
import { testid } from '../../util';
import { useContractListContext } from './contract-list-provider';
import { CartItem } from './contract-management.types';
import { formatCurrency, formatDataSubscriptionPlanWithTerm, formatPrice, getTermPrice } from './contract-management.utils';

const cellsx = {
  border: 'none',
  fontSize: '0.875rem',
  minHeight: '1.25em',
  wordBreak: 'break-word' as const,
  whiteSpace: 'normal' as const,
  verticalAlign: 'top',
};

interface ContractListWarningTableProps {
  displaySupportedCurrencies?: boolean;
  items: CartItem[];
}

const StyledTableCell = styled(TableCell)();

export const ContractListWarningTable: FC<ContractListWarningTableProps> = ({ displaySupportedCurrencies, items }) => {
  const { i18n, t } = useTranslation(['common', 'data-subscription', 'terms']);
  const { state: { selectedCurrency } } = useContractListContext();
  const smallScreen = useSmallScreen();

  if (smallScreen) {
    return (
      <Stack data-testid="contract-list-warning-table" spacing={2}>
        {items.map((item) => {
          const price = getTermPrice(item.dataSubscriptionPlan, item.term, selectedCurrency);
          return (
            <Paper key={item.thing._id} elevation={0} variant="outlined" sx={{ p: 2 }} data-testid={testid`contract-list-warning-table-item-${item.thing._id}`}>
              <Stack spacing={1}>
                <Stack>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>{t('terms:thing', { count: T_ONE })}</Typography>
                  <FetchOneOfAll
                    dataType={CacheDataTypes.THING_TYPE}
                    id={item.thing.thingTypeId}
                    renderFactory={(thingType: ThingType) => <Typography variant="body2">{thingType.display}</Typography>}
                  />
                  <Typography style={{ fontSize: '0.875rem', wordBreak: 'break-word' }}>{item.thing.display}</Typography>
                </Stack>
                <Stack>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>{t('common:page.contract-management.switch-currency-dialog.plan.labels')}</Typography>
                  <Typography style={{ fontSize: '0.875rem', wordBreak: 'break-word' }}>{formatDataSubscriptionPlanWithTerm(i18n, item.dataSubscriptionPlan, item.term)}</Typography>
                  {price !== null && <Typography variant="body2">{formatPrice(i18n, selectedCurrency, price)}</Typography>}
                </Stack>
                {displaySupportedCurrencies && (
                  <Stack>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>{t('common:page.contract-management.switch-currency-dialog.supported-currencies.labels')}</Typography>
                    <Typography style={{ fontSize: '0.875rem', wordBreak: 'break-word' }}>{item.dataSubscriptionPlan.supportedCurrencies.map((currency) => formatCurrency(i18n, currency)).join(', ')}</Typography>
                  </Stack>
                )}
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    );
  }

  return (
    <TableContainer>
      <Table data-testid="contract-list-warning-table" size="small">
        <TableHead>
          <TableRow>
            <StyledTableCell sx={{ ...cellsx, pl: 0 }}><Typography fontWeight={500}>{t('terms:thing', { count: T_ONE })}</Typography></StyledTableCell>
            <StyledTableCell sx={cellsx}><Typography fontWeight={500}>{t('common:page.contract-management.switch-currency-dialog.plan.labels')}</Typography></StyledTableCell>
            {displaySupportedCurrencies && (
              <StyledTableCell sx={{ ...cellsx, pr: 0 }}><Typography fontWeight={500}>{t('common:page.contract-management.switch-currency-dialog.supported-currencies.labels')}</Typography></StyledTableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => {
            const price = getTermPrice(item.dataSubscriptionPlan, item.term, selectedCurrency);
            return (
              <TableRow key={item.thing._id} data-testid={testid`contract-list-warning-table-item-${item.thing._id}`}>
                <StyledTableCell sx={{ ...cellsx, pl: 0 }}>
                  <Stack>
                    <FetchOneOfAll
                      dataType={CacheDataTypes.THING_TYPE}
                      id={item.thing.thingTypeId}
                      renderFactory={(thingType: ThingType) => <Typography variant='body2'>{thingType.display}</Typography>}
                    />
                    <Typography>{item.thing.display}</Typography>
                  </Stack>
                </StyledTableCell>
                <StyledTableCell sx={cellsx}>
                  <Stack>
                    <Typography>{formatDataSubscriptionPlanWithTerm(i18n, item.dataSubscriptionPlan, item.term)}</Typography>
                    <Typography variant="body2">{price !== null ? formatPrice(i18n, selectedCurrency, price) : null}</Typography>
                  </Stack>
                </StyledTableCell>
                {displaySupportedCurrencies && (
                  <StyledTableCell sx={{ ...cellsx, pr: 0 }}>
                    <Typography>
                      {item.dataSubscriptionPlan.supportedCurrencies.map((currency) => formatCurrency(i18n, currency)).join(', ')}
                    </Typography>
                  </StyledTableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
